import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const { FiCopy, FiCheck, FiClipboard } = FiIcons;

function CitationResult({ citation, reference, livePreview = false, sourceType = 'Documento' }) {
  const [copiedType, setCopiedType] = useState(null);
  const resultRef = useRef(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  const copyToClipboard = async (text, type) => {
    try {
      // Eliminar las etiquetas HTML al copiar
      const plainText = text.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(plainText);
      setCopiedType(type);

      // Highlight effect
      setIsHighlighted(true);
      setTimeout(() => {
        setIsHighlighted(false);
        setTimeout(() => setCopiedType(null), 1000);
      }, 1000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  // Reset highlight when citation or reference changes
  useEffect(() => {
    setIsHighlighted(false);
    setCopiedType(null);
  }, [citation, reference]);

  // Si es vista previa en vivo y no hay datos completos, mostrar un mensaje
  if (livePreview && (!citation || !reference)) {
    return (
      <motion.div 
        className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 dark:bg-gray-800 dark:border-gray-700"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 dark:text-gray-100">Vista previa</h3>
        <div className="flex items-center justify-center h-24 sm:h-32 text-gray-500 italic dark:text-gray-400 bg-gray-50 rounded-lg dark:bg-gray-700/50">
          <p className="text-sm sm:text-base">Complete los campos obligatorios para generar la vista previa</p>
        </div>
      </motion.div>
    );
  }

  // Si no es vista previa y no hay datos, no mostrar nada
  if (!livePreview && (!citation && !reference)) {
    return null;
  }

  return (
    <motion.div 
      className={`bg-white rounded-lg border ${isHighlighted ? 'border-[#F54CC8]' : 'border-gray-200'} p-4 sm:p-6 space-y-4 sm:space-y-6 shadow-md dark:bg-gray-800 dark:border-gray-700`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      ref={resultRef}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
          {livePreview ? "Vista previa" : "Resultado"}
        </h3>
        {!livePreview && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="text-xs sm:text-sm flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300"
            onClick={() => {
              const fullText = `${citation?.narrative || ''}\n${citation?.parenthetical || ''}\n\n${reference?.text || ''}`;
              copyToClipboard(fullText, 'all');
            }}
          >
            <SafeIcon icon={copiedType === 'all' ? FiCheck : FiClipboard} className="w-3 h-3 sm:w-4 sm:h-4" />
            <span>{copiedType === 'all' ? 'Copiado' : 'Copiar todo'}</span>
          </motion.button>
        )}
      </div>

      {citation && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
              <span>Cita</span>
              <AnimatePresence>
                {isHighlighted && copiedType && copiedType.includes('narrative') && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                  >
                    <SafeIcon icon={FiCheck} className="w-3 h-3 mr-0.5" />
                    Copiado
                  </motion.span>
                )}
              </AnimatePresence>
            </h4>
          </div>

          <div className="citation-result bg-gray-50 p-3 sm:p-4 rounded-md dark:bg-gray-700">
            {/* Solo mostrar cita narrativa si existe */}
            {citation.narrative && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">Narrativa:</span>
                  <motion.button
                    onClick={() => copyToClipboard(citation.narrative, 'narrative')}
                    className={`flex items-center space-x-1 px-2 py-0.5 text-xs ${
                      copiedType === 'narrative'
                        ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500'
                    } rounded-md transition-colors`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={copiedType === 'narrative' ? FiCheck : FiCopy} className="w-3 h-3" />
                    <span>{copiedType === 'narrative' ? 'Copiado' : 'Copiar'}</span>
                  </motion.button>
                </div>
                <div className="mb-4 dark:text-gray-200 text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: citation.narrative }} />
                </div>
              </>
            )}

            {/* Siempre mostrar cita parentética */}
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">Parentética:</span>
              <motion.button
                onClick={() => copyToClipboard(citation.parenthetical, 'parenthetical')}
                className={`flex items-center space-x-1 px-2 py-0.5 text-xs ${
                  copiedType === 'parenthetical'
                    ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                    : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500'
                } rounded-md transition-colors`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <SafeIcon icon={copiedType === 'parenthetical' ? FiCheck : FiCopy} className="w-3 h-3" />
                <span>{copiedType === 'parenthetical' ? 'Copiado' : 'Copiar'}</span>
              </motion.button>
            </div>
            <div className="dark:text-gray-200 text-sm sm:text-base">
              <div dangerouslySetInnerHTML={{ __html: citation.parenthetical }} />
            </div>

            {/* Mostrar nota informativa si no hay cita narrativa */}
            {!citation.narrative && (
              <motion.div
                className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900 dark:border-yellow-800 dark:text-yellow-200"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-xs text-yellow-800 dark:text-yellow-200">
                  <strong>Nota:</strong> Cuando el título funciona como autor, solo se genera cita parentética.
                </p>
              </motion.div>
            )}

            {/* Mostrar citas subsecuentes si existen */}
            {citation.narrativeSubsequent && citation.narrativeSubsequent !== citation.narrative && (
              <motion.div
                className="mt-4 border-t border-gray-200 pt-4 dark:border-gray-600"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    Narrativa (citas subsecuentes):
                  </span>
                  <motion.button
                    onClick={() => copyToClipboard(citation.narrativeSubsequent, 'narrativeSubsequent')}
                    className={`flex items-center space-x-1 px-2 py-0.5 text-xs ${
                      copiedType === 'narrativeSubsequent'
                        ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500'
                    } rounded-md transition-colors`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={copiedType === 'narrativeSubsequent' ? FiCheck : FiCopy} className="w-3 h-3" />
                    <span>{copiedType === 'narrativeSubsequent' ? 'Copiado' : 'Copiar'}</span>
                  </motion.button>
                </div>
                <div className="mb-4 dark:text-gray-200 text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: citation.narrativeSubsequent }} />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300">
                    Parentética (citas subsecuentes):
                  </span>
                  <motion.button
                    onClick={() => copyToClipboard(citation.parentheticalSubsequent, 'parentheticalSubsequent')}
                    className={`flex items-center space-x-1 px-2 py-0.5 text-xs ${
                      copiedType === 'parentheticalSubsequent'
                        ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-500'
                    } rounded-md transition-colors`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={copiedType === 'parentheticalSubsequent' ? FiCheck : FiCopy} className="w-3 h-3" />
                    <span>{copiedType === 'parentheticalSubsequent' ? 'Copiado' : 'Copiar'}</span>
                  </motion.button>
                </div>
                <div className="dark:text-gray-200 text-sm sm:text-base">
                  <div dangerouslySetInnerHTML={{ __html: citation.parentheticalSubsequent }} />
                </div>
              </motion.div>
            )}
          </div>
        </div>
      )}

      {reference && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
              <span>Referencia</span>
              <AnimatePresence>
                {isHighlighted && copiedType === 'reference' && (
                  <motion.span
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                  >
                    <SafeIcon icon={FiCheck} className="w-3 h-3 mr-0.5" />
                    Copiado
                  </motion.span>
                )}
              </AnimatePresence>
            </h4>
            <motion.button
              onClick={() => copyToClipboard(reference.text, 'reference')}
              className={`flex items-center space-x-1 px-2 py-1 text-xs sm:text-sm ${
                copiedType === 'reference'
                  ? 'bg-green-100 text-green-700 dark:bg-green-700 dark:text-green-100'
                  : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600'
              } rounded-md transition-colors`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={copiedType === 'reference' ? FiCheck : FiCopy} className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{copiedType === 'reference' ? 'Copiado' : 'Copiar'}</span>
            </motion.button>
          </div>
          <motion.div
            className="citation-result bg-gray-50 rounded-md overflow-auto dark:bg-gray-700"
            animate={{
              boxShadow: isHighlighted && copiedType === 'reference' ? [
                '0 0 0 0 rgba(245,76,200,0)',
                '0 0 0 4px rgba(245,76,200,0.3)',
                '0 0 0 0 rgba(245,76,200,0)',
              ] : 'none',
            }}
            transition={{ duration: 1.5 }}
          >
            <div className="p-3 sm:p-4 text-justify dark:text-gray-200 text-sm sm:text-base">
              <div className="hanging-indent" dangerouslySetInnerHTML={{ __html: reference.html || reference.text }} />
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
}

export default CitationResult;